import { useEffect, useRef, useState } from 'react'
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom'

import { DebugMenuTrigger } from '@/debug/DebugMenu'
import { isDebugEnvironment } from '@/debug/debugStore'
import { AppRouter } from '@/router/AppRouter'
import { LoadingSpinner } from '@/shared-ui/LoadingSpinner'
import { useAppStore } from '@/store/appStore'

interface AppContentProps {
  onReady?: () => void
}

function AppContent({ onReady }: AppContentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { runBootstrap, isInitialized, isLoading, bootstrapRoute } =
    useAppStore()
  const hasNavigated = useRef(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    runBootstrap()
  }, [runBootstrap])

  useEffect(() => {
    if (isInitialized && bootstrapRoute && !hasNavigated.current) {
      hasNavigated.current = true
      // When bootstrap clears to /home and the user is already on a specific
      // route (e.g. /dashboard during dev), preserve that URL instead of
      // overwriting it. Only redirect to /home when coming from the root.
      const isAtRoot = location.pathname === '/'
      const target =
        bootstrapRoute === '/home' && !isAtRoot
          ? location.pathname
          : bootstrapRoute
      navigate(target, { replace: true })
      setIsReady(true)
      onReady?.()
    }
  }, [isInitialized, bootstrapRoute, location.pathname, navigate, onReady])

  // Keep spinner until after bootstrap navigation completes so AppRouter
  // never sees the launch URL path ("/") and triggers its root redirect.
  if (isLoading || !isInitialized || !isReady) {
    return <LoadingSpinner fullscreen />
  }

  return <AppRouter />
}

interface AppProps {
  onReady?: () => void
}

function App({ onReady }: AppProps) {
  const content = (
    <BrowserRouter>
      <AppContent onReady={onReady} />
    </BrowserRouter>
  )

  if (isDebugEnvironment) {
    return (
      <>
        {content}
        <DebugMenuTrigger />
      </>
    )
  }

  return content
}

export default App
